import React, { useState, useMemo, useEffect, useCallback, useContext, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  History, ArrowRight, ArrowLeft, ShieldAlert, ShieldOff,
  PlusCircle, RefreshCw, Filter, X, Trash2, Package, ChevronLeft, ChevronRight, Download,
  ShoppingCart, Factory, ClipboardCheck, Truck, CheckCircle2, Warehouse,
} from "lucide-react";
import { ClientGroupsContext } from "@/App";
import { exportHistoryLog } from "@/components/exportHistoryLog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PAGE_SIZE = 100;

const ACTION_ICONS = {
  "Stage Advanced": { icon: ArrowRight, color: "#22c55e" },
  "Sent Back":      { icon: ArrowLeft, color: "#f59e0b" },
  "Blocked":        { icon: ShieldAlert, color: "#ef4444" },
  "Unblocked":      { icon: ShieldOff, color: "#3b82f6" },
  "Status Updated": { icon: RefreshCw, color: "#a855f7" },
  "Created":        { icon: PlusCircle, color: "#f97316" },
  "Edited":         { icon: RefreshCw, color: "#14b8a6" },
  "Deleted":        { icon: Trash2, color: "#ef4444" },
  "Bulk Import":    { icon: Package, color: "#f97316" },
  "Bulk Upsert":    { icon: Package, color: "#f97316" },
  "Bulk Updated":   { icon: Package, color: "#f59e0b" },
};

const ACTION_TYPES = [
  "Stage Advanced", "Sent Back", "Blocked", "Unblocked", "Status Updated",
  "Created", "Edited", "Deleted", "Bulk Import", "Bulk Upsert", "Bulk Updated",
];
const STAGES = ["PO Received", "Procurement", "Manufacturing", "Quality Control", "Dispatch", "Completed"];

// ── Stage vocabulary — mirrors the Live Activity widget (dashboard/
// LiveUpdatesCard.js StagePill + dashboard/utils) so a stage looks and reads
// the same here as in the global feed. ──────────────────────────────────────
const STAGE_COLORS = {
  "PO Received":     { color: "#3b82f6" },
  "Procurement":     { color: "#f97316" },
  "Manufacturing":   { color: "#a855f7" },
  "Quality Control": { color: "#14b8a6" },
  "Dispatch":        { color: "#f59e0b" },
  "Completed":       { color: "#94a3b8" },
  "Stores":          { color: "#f97316" },
};

const STAGE_ICONS = {
  "PO Received":     Package,
  "Procurement":     ShoppingCart,
  "Manufacturing":   Factory,
  "Quality Control": ClipboardCheck,
  "Dispatch":        Truck,
  "Completed":       CheckCircle2,
  "Stores":          Warehouse,
};

const STAGE_ABBR = {
  "PO Received": "POR", "Procurement": "Proc",
  "Manufacturing": "Mfg", "Quality Control": "QC",
  "Dispatch": "Disp", "Completed": "Done", "Stores": "Store",
};

const STAGE_PATHS = {
  "PO Received": "/po-received",
  "Procurement": "/procurement",
  "Manufacturing": "/manufacturing",
  "Quality Control": "/qc-check",
  "Dispatch": "/dispatch",
  "Completed": "/completed",
  "Stores": "/stores",
};

/* StagePill — the SAME stage badge the Live Activity feed uses (glassy hairline
 * pill, stage-tinted, with its stage icon). Borrowed verbatim so the History
 * Log and the global feed never drift into two generations of badge. */
function StagePill({ stage }) {
  if (!stage) return null;
  const sc = STAGE_COLORS[stage] || { color: "#64748b" };
  const Icon = STAGE_ICONS[stage];
  return (
    <span
      className="inline-flex items-center justify-center gap-1 text-[10px] font-semibold tracking-wide px-2 py-0.5 rounded-full border rl-pill leading-none whitespace-nowrap shrink-0"
      style={{ background: `${sc.color}0d`, color: sc.color, borderColor: `${sc.color}59` }}
    >
      {Icon && <Icon className="w-2.5 h-2.5" />}
      {STAGE_ABBR[stage] ?? stage.slice(0, 4)}
    </span>
  );
}

/* PartPoBlock — the canonical part#/PO/LN identity block from the stage tables
 * (StagePage/StageRowDesktop.js, "Part # / Rev" cell): the mono part chip on
 * top, then a tight PO / LN two-column grid with whisper-small labels. Reused
 * so an item reads the same in History as it does on its stage page. */
function PartPoBlock({ entry, compact = false }) {
  const partChipFontCls = compact ? "text-[11px]" : "text-[12px]";
  const poRowFontCls = compact ? "text-[9px]" : "text-[10px]";
  return (
    <div className="space-y-0.5 min-w-0">
      {(entry.part_number || entry.revision) && (
        <div className="flex items-center gap-1.5 flex-wrap">
          {entry.part_number && (
            <span className={`font-mono-data ${partChipFontCls} px-2 py-0.5 rounded-md rl-carved bg-[hsl(var(--muted))]/40 text-[hsl(var(--foreground))] font-semibold inline-block max-w-full [overflow-wrap:anywhere] [word-break:break-word]`}>{entry.part_number}</span>
          )}
          {entry.revision && (
            <span className={`font-mono-data ${partChipFontCls} px-2 py-0.5 rounded-md rl-carved bg-[hsl(var(--muted))]/50 text-[hsl(var(--muted-foreground))]`} title={`Revision ${entry.revision}`}>{entry.revision}</span>
          )}
        </div>
      )}
      {entry.po_number && (
        <div className={`grid grid-cols-[18px_minmax(0,1fr)] gap-x-1.5 gap-y-0.5 items-baseline ${poRowFontCls} font-mono-data text-[hsl(var(--foreground))]/85 leading-tight`}>
          <span className="text-right text-[8px] font-bold uppercase tracking-wide text-[hsl(var(--muted-foreground))]/70 leading-none">PO</span>
          <span className="font-semibold [overflow-wrap:anywhere]">{entry.po_number}</span>
          {entry.line_number && (
            <>
              <span className="text-right text-[8px] font-bold uppercase tracking-wide text-[hsl(var(--muted-foreground))]/70 leading-none">LN</span>
              <span className="text-[hsl(var(--foreground))]/70">{entry.line_number}</span>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function formatTime(iso) {
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now - d;
  const diffSec = Math.floor(diffMs / 1000);
  if (diffSec < 60) return `${diffSec}s ago`;
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

function formatFullTime(iso) {
  return new Date(iso).toLocaleString("en-US", {
    month: "short", day: "numeric", year: "numeric",
    hour: "2-digit", minute: "2-digit", second: "2-digit"
  });
}

export default function Changelog() {
  const navigate = useNavigate();
  const [changelog, setChangelog] = useState([]);   // current page only
  const [total, setTotal] = useState(0);             // entries matching the filters
  const [unfilteredTotal, setUnfilteredTotal] = useState(0);
  const [historyUsers, setHistoryUsers] = useState([]);
  const [exporting, setExporting] = useState(false);
  const [clients, setClients] = useState([]);
  const [userList, setUserList] = useState([]);
  const [loadingLog, setLoadingLog] = useState(true);
  const [filterUser, setFilterUser] = useState("all");
  const [filterClient, setFilterClient] = useState("all");
  const [filterStage, setFilterStage] = useState("all");
  const [filterAction, setFilterAction] = useState("all");
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");
  const [page, setPage] = useState(1);

  // Filters → /changelog/search params. Dates are the viewer's local day
  // bounds, sent as ISO instants so they compare against stored UTC stamps.
  const filterParams = useMemo(() => {
    const p = {};
    if (filterUser !== "all") p.user = filterUser;
    if (filterClient !== "all") p.client = filterClient;
    if (filterStage !== "all") p.stage = filterStage;
    if (filterAction !== "all") p.action = filterAction;
    if (filterDateFrom) {
      const from = new Date(filterDateFrom);
      from.setHours(0, 0, 0, 0);
      p.date_from = from.toISOString();
    }
    if (filterDateTo) {
      const to = new Date(filterDateTo);
      to.setHours(23, 59, 59, 999);
      p.date_to = to.toISOString();
    }
    return p;
  }, [filterUser, filterClient, filterStage, filterAction, filterDateFrom, filterDateTo]);

  // Server pages + filters the log — pulling all 30k+ entries made the page
  // take 5–7 s to open. A newer request supersedes any still in flight.
  const reqSeq = useRef(0);
  const fetchChangelog = useCallback(async () => {
    const seq = ++reqSeq.current;
    try {
      setLoadingLog(true);
      const res = await axios.get(`${API}/changelog/search`, {
        params: { ...filterParams, page, page_size: PAGE_SIZE, include_users: historyUsers.length === 0 },
      });
      if (seq !== reqSeq.current) return;
      setChangelog(res.data.entries || []);
      setTotal(res.data.total || 0);
      if (Object.keys(filterParams).length === 0) setUnfilteredTotal(res.data.total || 0);
      if (res.data.users) setHistoryUsers(res.data.users);
    } catch (e) {
      console.error("Failed to load changelog:", e);
    } finally {
      if (seq === reqSeq.current) setLoadingLog(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterParams, page]);

  const handleExport = async () => {
    try {
      setExporting(true);
      const res = await axios.get(`${API}/changelog/search`, {
        params: { ...filterParams, page: 1, page_size: 50000 },
      });
      exportHistoryLog(res.data.entries || []);
    } catch (e) {
      console.error("Failed to export changelog:", e);
    } finally {
      setExporting(false);
    }
  };

  const fetchClients = useCallback(async () => {
    try { const res = await axios.get(`${API}/clients`); setClients(res.data || []); }
    catch (e) { /* non-critical */ }
  }, []);

  const { clientGroups } = useContext(ClientGroupsContext) || {};

  const clientLogoMap = useMemo(() => {
    const map = {};
    if (!clients || clients.length === 0) return map;
    clients.forEach(c => {
      if (!c.name) return;
      if (c.logo_svg && c.logo_svg.trim()) {
        map[c.name] = c.logo_svg;
        return;
      }
      const groups = clientGroups || [];
      const group = groups.find(g => g.client_ids && g.client_ids.includes(c.id));
      if (group && group.logo_svg && group.logo_svg.trim()) {
        map[c.name] = group.logo_svg;
      }
    });
    return map;
  }, [clients, clientGroups]);

  const fetchUsers = useCallback(async () => {
    try {
      // presence endpoint is available to all authenticated users and returns name + role
      const res = await axios.get(`${API}/auth/presence`);
      setUserList(res.data || []);
    } catch (e) { /* non-critical */ }
  }, []);

  useEffect(() => {
    fetchClients();
    fetchUsers();
  }, [fetchClients, fetchUsers]);

  useEffect(() => { fetchChangelog(); }, [fetchChangelog]);

  const hasFilters = filterUser !== "all" || filterClient !== "all" || filterStage !== "all" || filterAction !== "all" || filterDateFrom || filterDateTo;

  const clearFilters = () => {
    setFilterUser("all");
    setFilterClient("all");
    setFilterStage("all");
    setFilterAction("all");
    setFilterDateFrom("");
    setFilterDateTo("");
    setPage(1);
  };

  // A row is navigable when it has a part# or PO to search AND its logged stage
  // maps to a stage page. Bulk ops + Auth/Admin entries have no single target,
  // so they stay inert (no misleading hover). Mirrors the Live Activity widget's
  // goToItem: jump to the entry's logged stage and search the part#/PO there.
  const navTargetFor = (entry) => {
    if (entry.is_bulk) return null;
    const path = STAGE_PATHS[entry.stage];
    const q = entry.part_number || entry.po_number;
    return path && q ? `${path}?q=${encodeURIComponent(q)}` : null;
  };
  const goToEntry = (entry) => {
    const target = navTargetFor(entry);
    if (target) navigate(target);
  };

  // Reset page when filters change
  useEffect(() => { setPage(1); }, [filterUser, filterClient, filterStage, filterAction, filterDateFrom, filterDateTo]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageStart = (safePage - 1) * PAGE_SIZE;
  const pageEntries = changelog;

  // Dynamic client colors (fallback)
  const clientColor = (name) => {
    const c = clients.find(x => x.name === name);
    return c?.color || "#94a3b8";
  };

  // Unique users in history (merged with active users from DB)
  const userOptions = useMemo(() => {
    const s = new Set();
    userList.forEach(u => { if (u.name) s.add(u.name); });
    historyUsers.forEach(u => { if (u) s.add(u); });
    return Array.from(s).sort((a, b) => a.localeCompare(b));
  }, [userList, historyUsers]);

  const clientOptions = useMemo(
    () => (clients || []).map(c => c.name).filter(Boolean).sort(),
    [clients]
  );

  return (
    <div className="space-y-4" data-testid="changelog-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-1 h-8 rounded-full bg-orange-500" />
          <div>
            <h2 className="text-2xl font-bold tracking-tight">History Log</h2>
            <p className="text-sm text-[hsl(var(--muted-foreground))]">
              <span className="font-mono-data">{total}</span> entries
              {hasFilters && unfilteredTotal > 0 && unfilteredTotal !== total && (
                <span className="ml-1 text-xs">({unfilteredTotal} total)</span>
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleExport}
            disabled={total === 0 || exporting}
            className="rl-frost-btn flex items-center gap-1.5 text-xs px-3.5 py-1.5 rounded-full text-orange-600 dark:text-orange-400 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="export-changelog-btn"
            title="Export the current filtered view to CSV"
          >
            <Download className="w-3.5 h-3.5" /> Export view
          </button>
          <button
            onClick={fetchChangelog}
            className="rl-frost-btn flex items-center gap-1.5 text-xs px-3.5 py-1.5 rounded-full text-[hsl(var(--muted-foreground))]"
            data-testid="refresh-changelog-btn"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="rl-glass-edge rounded-xl p-4 bg-[hsl(var(--card))]" data-testid="changelog-filters">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="w-4 h-4 text-[hsl(var(--muted-foreground))]" />
          <span className="text-xs font-bold uppercase tracking-widest text-[hsl(var(--muted-foreground))]">Filters</span>
          {hasFilters && (
            <button onClick={clearFilters} className="rl-frost-btn ml-auto flex items-center gap-1 text-xs px-2.5 py-1 rounded-full text-orange-500 hover:text-orange-400" data-testid="clear-filters-btn">
              <X className="w-3 h-3" /> Clear all
            </button>
          )}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          <div>
            <label className="text-[10px] font-medium uppercase tracking-wider text-[hsl(var(--muted-foreground))] mb-1 block">User</label>
            <Select value={filterUser} onValueChange={setFilterUser}>
              <SelectTrigger className="rl-frost-btn h-8 text-xs border-0 rounded-full px-3" data-testid="filter-user">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rl-frost rounded-xl [&_[data-state=checked]]:text-orange-600 [&_[role=option][data-highlighted]]:bg-orange-500/10 [&_[role=option][data-highlighted]]:text-orange-600 dark:[&_[role=option][data-highlighted]]:text-orange-400">
                <SelectItem value="all">All Users</SelectItem>
                {userOptions.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] font-medium uppercase tracking-wider text-[hsl(var(--muted-foreground))] mb-1 block">Client</label>
            <Select value={filterClient} onValueChange={setFilterClient}>
              <SelectTrigger className="rl-frost-btn h-8 text-xs border-0 rounded-full px-3" data-testid="filter-client">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rl-frost rounded-xl [&_[data-state=checked]]:text-orange-600 [&_[role=option][data-highlighted]]:bg-orange-500/10 [&_[role=option][data-highlighted]]:text-orange-600 dark:[&_[role=option][data-highlighted]]:text-orange-400">
                <SelectItem value="all">All Clients</SelectItem>
                {clientOptions.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] font-medium uppercase tracking-wider text-[hsl(var(--muted-foreground))] mb-1 block">Stage</label>
            <Select value={filterStage} onValueChange={setFilterStage}>
              <SelectTrigger className="rl-frost-btn h-8 text-xs border-0 rounded-full px-3" data-testid="filter-stage">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rl-frost rounded-xl [&_[data-state=checked]]:text-orange-600 [&_[role=option][data-highlighted]]:bg-orange-500/10 [&_[role=option][data-highlighted]]:text-orange-600 dark:[&_[role=option][data-highlighted]]:text-orange-400">
                <SelectItem value="all">All Stages</SelectItem>
                {STAGES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] font-medium uppercase tracking-wider text-[hsl(var(--muted-foreground))] mb-1 block">Action</label>
            <Select value={filterAction} onValueChange={setFilterAction}>
              <SelectTrigger className="rl-frost-btn h-8 text-xs border-0 rounded-full px-3" data-testid="filter-action">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rl-frost rounded-xl [&_[data-state=checked]]:text-orange-600 [&_[role=option][data-highlighted]]:bg-orange-500/10 [&_[role=option][data-highlighted]]:text-orange-600 dark:[&_[role=option][data-highlighted]]:text-orange-400">
                <SelectItem value="all">All Actions</SelectItem>
                {ACTION_TYPES.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] font-medium uppercase tracking-wider text-[hsl(var(--muted-foreground))] mb-1 block">From</label>
            <input
              type="date"
              value={filterDateFrom}
              onChange={e => setFilterDateFrom(e.target.value)}
              className="rl-frost-btn w-full h-8 px-3 text-xs font-mono-data rounded-full border-0 text-[hsl(var(--foreground))] focus:outline-none focus:ring-2 focus:ring-orange-500"
              data-testid="filter-date-from"
            />
          </div>
          <div>
            <label className="text-[10px] font-medium uppercase tracking-wider text-[hsl(var(--muted-foreground))] mb-1 block">To</label>
            <input
              type="date"
              value={filterDateTo}
              onChange={e => setFilterDateTo(e.target.value)}
              className="rl-frost-btn w-full h-8 px-3 text-xs font-mono-data rounded-full border-0 text-[hsl(var(--foreground))] focus:outline-none focus:ring-2 focus:ring-orange-500"
              data-testid="filter-date-to"
            />
          </div>
        </div>
      </div>

      {/* Log Table */}
      {loadingLog && changelog.length === 0 ? (
        <div className="flex items-center justify-center py-16">
          <div className="w-6 h-6 border-2 border-orange-600/30 border-t-orange-600 rounded-full animate-spin" />
        </div>
      ) : total === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-[hsl(var(--muted-foreground))]" data-testid="changelog-empty">
          <History className="w-12 h-12 mb-3 opacity-30" />
          <p className="text-sm font-medium">No history log entries</p>
          <p className="text-xs mt-1">Try adjusting filters or perform actions on line items</p>
        </div>
      ) : (
        <>
          <div className={`rl-glass-edge rounded-xl overflow-hidden bg-[hsl(var(--card))] hidden md:block transition-opacity ${loadingLog ? "opacity-60" : ""}`} data-testid="changelog-table">
            {/* Accent line — same anatomy as the stage card */}
            <div className="h-[2px] bg-orange-500" />
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[hsl(var(--border))] bg-[hsl(var(--muted))]/30">
                    <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-medium text-[hsl(var(--muted-foreground))] w-[130px]">Time</th>
                    <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-medium text-[hsl(var(--muted-foreground))] w-[120px]">User</th>
                    <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-medium text-[hsl(var(--muted-foreground))] w-[88px]">Stage</th>
                    <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-medium text-[hsl(var(--muted-foreground))]">Item</th>
                    <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-medium text-[hsl(var(--muted-foreground))] w-[170px]">Part # / Rev</th>
                    <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-medium text-[hsl(var(--muted-foreground))] w-[140px]">Action</th>
                    <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-medium text-[hsl(var(--muted-foreground))]">Change</th>
                  </tr>
                </thead>
                <tbody>
                  {pageEntries.map(entry => {
                    const ai = ACTION_ICONS[entry.action] || { icon: RefreshCw, color: "#94a3b8" };
                    const Icon = ai.icon;
                    const cc = clientColor(entry.client);
                    const isBulk = !!entry.is_bulk;
                    const navTarget = navTargetFor(entry);
                    const clickable = !!navTarget;
                    return (
                      <tr
                        key={entry.id}
                        onClick={clickable ? () => goToEntry(entry) : undefined}
                        title={clickable ? `Open ${entry.part_number || entry.po_number} in ${entry.stage}` : undefined}
                        className={`border-b border-[hsl(var(--border))]/50 transition-colors ${clickable ? "changelog-row cursor-pointer" : ""}`}
                        data-testid={`changelog-entry-${entry.id}`}
                      >
                        <td className="px-4 py-3 whitespace-nowrap" title={formatFullTime(entry.timestamp)}>
                          <span className="font-mono-data text-xs text-[hsl(var(--muted-foreground))]">{formatTime(entry.timestamp)}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="rl-pill inline-block text-xs font-medium px-2.5 py-0.5 rounded-full border border-[hsl(var(--border))] bg-[hsl(var(--card))] text-[hsl(var(--foreground))]/85 whitespace-nowrap">{entry.user}</span>
                        </td>
                        <td className="px-4 py-3 align-middle">
                          <StagePill stage={entry.stage} />
                        </td>
                        <td className="px-4 py-3">
                          {isBulk ? (
                            <div className="flex items-start gap-2 flex-wrap">
                              <span className="text-sm text-[hsl(var(--foreground))]">{entry.item_description}</span>
                              {entry.bulk_count > 0 && (
                                <span className="font-mono-data text-[10px] px-1.5 py-0.5 rounded bg-orange-500/10 text-orange-500 border border-orange-500/20 whitespace-nowrap">{entry.bulk_count} item{entry.bulk_count !== 1 ? 's' : ''}</span>
                              )}
                            </div>
                          ) : (
                            // Client identity on top (colored + logo, like StagePage's
                            // description cell), item description beneath.
                            <div className="space-y-0.5">
                              {entry.client && (() => {
                                const logo = clientLogoMap[entry.client];
                                const initials = entry.client.split(" ").slice(0, 2).map(w => w[0]).join("");
                                return (
                                  <span className="inline-flex items-center gap-1" style={{ color: cc }}>
                                    {logo ? (
                                      <span
                                        className="inline-flex items-center justify-center shrink-0 [&>svg]:w-full [&>svg]:h-full"
                                        style={{ width: 14, height: 14 }}
                                        dangerouslySetInnerHTML={{ __html: logo }}
                                      />
                                    ) : (
                                      <span
                                        className="inline-flex items-center justify-center shrink-0 rounded-full text-[6px] font-bold leading-none"
                                        style={{ width: 14, height: 14, color: cc, backgroundColor: cc + "20" }}
                                      >
                                        {initials}
                                      </span>
                                    )}
                                    <span className="text-[12px] font-semibold">{entry.client}</span>
                                  </span>
                                );
                              })()}
                              <span className="block text-sm font-medium leading-snug text-[hsl(var(--foreground))] break-words">{entry.item_description}</span>
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 align-middle">
                          {isBulk ? (
                            <span className="text-[10px] font-mono-data text-[hsl(var(--muted-foreground))]/70 truncate block max-w-[160px]" title={entry.new_value}>
                              {entry.new_value || "—"}
                            </span>
                          ) : (entry.part_number || entry.po_number) ? (
                            <PartPoBlock entry={entry} />
                          ) : (
                            <span className="text-[10px] text-[hsl(var(--muted-foreground))]/60">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className="flex items-center gap-1.5">
                            <Icon className="w-3.5 h-3.5 shrink-0" style={{ color: ai.color }} />
                            <span className="text-xs font-semibold" style={{ color: ai.color }}>{entry.action}</span>
                            {isBulk && (
                              <span className="text-[9px] font-bold uppercase tracking-wider px-1 py-0 rounded bg-orange-500/15 text-orange-500 border border-orange-500/30">Bulk</span>
                            )}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          {isBulk ? (
                            <span className="text-xs text-[hsl(var(--muted-foreground))] italic">Bulk operation</span>
                          ) : entry.old_value && entry.new_value ? (
                            <div className="flex items-center gap-2 text-xs">
                              <span className="rl-pill px-2 py-0.5 rounded-full border border-[hsl(var(--border))] bg-[hsl(var(--muted))]/40 text-[hsl(var(--foreground))]/60 font-mono-data line-through decoration-[hsl(var(--muted-foreground))]/60">{entry.old_value}</span>
                              <ArrowRight className="w-3 h-3 text-[hsl(var(--muted-foreground))] shrink-0" />
                              <span className="rl-pill px-2 py-0.5 rounded-full border border-emerald-500/40 bg-emerald-500/5 text-emerald-700 dark:text-emerald-400 font-mono-data font-semibold">{entry.new_value}</span>
                            </div>
                          ) : entry.new_value ? (
                            <span className="rl-pill text-xs px-2 py-0.5 rounded-full border border-emerald-500/40 bg-emerald-500/5 text-emerald-700 dark:text-emerald-400 font-mono-data font-semibold">{entry.new_value}</span>
                          ) : (
                            <span className="text-xs text-[hsl(var(--muted-foreground))]">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>


          {/* Mobile cards — md:hidden, dense */}
          <div className="md:hidden space-y-1.5" data-testid="changelog-cards">
            {pageEntries.map(entry => {
              const ai = ACTION_ICONS[entry.action] || { icon: RefreshCw, color: "#94a3b8" };
              const Icon = ai.icon;
              const isBulk = !!entry.is_bulk;
              const navTarget = navTargetFor(entry);
              const clickable = !!navTarget;
              return (
                <div
                  key={entry.id}
                  onClick={clickable ? () => goToEntry(entry) : undefined}
                  className={`border border-[hsl(var(--border))] rounded-lg bg-[hsl(var(--card))] px-2.5 py-2 transition-colors ${clickable ? "cursor-pointer active:bg-orange-500/[0.06]" : ""}`}
                  data-testid={`changelog-card-${entry.id}`}
                >
                  <div className="flex items-center justify-between gap-2 mb-1.5">
                    <span className="flex items-center gap-1.5 min-w-0">
                      {!isBulk && entry.stage && <StagePill stage={entry.stage} />}
                      <Icon className="w-3.5 h-3.5 shrink-0" style={{ color: ai.color }} />
                      <span className="text-xs font-semibold truncate" style={{ color: ai.color }}>{entry.action}</span>
                      {isBulk && <span className="text-[9px] font-bold uppercase tracking-wider px-1 rounded bg-orange-500/15 text-orange-500 border border-orange-500/30 shrink-0">Bulk</span>}
                      <span className="text-[11px] font-medium px-1.5 py-0.5 rounded bg-[hsl(var(--muted))] truncate">{entry.user}</span>
                    </span>
                    <span className="font-mono-data text-[11px] text-[hsl(var(--muted-foreground))] shrink-0" title={formatFullTime(entry.timestamp)}>{formatTime(entry.timestamp)}</span>
                  </div>
                  {isBulk ? (
                    <div className="text-xs text-[hsl(var(--foreground))] flex items-center gap-2 flex-wrap">
                      {entry.item_description}
                      {entry.bulk_count > 0 && <span className="font-mono-data text-[10px] px-1.5 py-0.5 rounded bg-orange-500/10 text-orange-500 border border-orange-500/20">{entry.bulk_count} item{entry.bulk_count !== 1 ? 's' : ''}</span>}
                    </div>
                  ) : (
                    <div className="space-y-1">
                      {entry.client && <span className="block text-[11px] font-semibold text-[hsl(var(--foreground))]">{entry.client}</span>}
                      {entry.item_description && <span className="block text-xs text-[hsl(var(--foreground))]/90 break-words">{entry.item_description}</span>}
                      {(entry.part_number || entry.po_number) && <PartPoBlock entry={entry} compact />}
                    </div>
                  )}
                  {!isBulk && (entry.old_value || entry.new_value) && (
                    <div className="flex items-center gap-1.5 text-xs flex-wrap mt-1.5">
                      {entry.old_value && entry.new_value ? (
                        <>
                          <span className="px-1.5 py-0.5 rounded bg-[hsl(var(--muted))]/60 text-[hsl(var(--foreground))]/70 font-mono-data line-through">{entry.old_value}</span>
                          <ArrowRight className="w-3 h-3 text-[hsl(var(--muted-foreground))] shrink-0" />
                          <span className="px-1.5 py-0.5 rounded bg-emerald-600/15 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300 font-mono-data font-semibold">{entry.new_value}</span>
                        </>
                      ) : entry.new_value ? (
                        <span className="px-1.5 py-0.5 rounded bg-emerald-600/15 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300 font-mono-data font-semibold">{entry.new_value}</span>
                      ) : null}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between" data-testid="changelog-pagination">
              <p className="text-xs text-[hsl(var(--muted-foreground))]">
                Showing <span className="font-mono-data text-[hsl(var(--foreground))]">{pageStart + 1}</span>
                &nbsp;–&nbsp;
                <span className="font-mono-data text-[hsl(var(--foreground))]">{Math.min(pageStart + PAGE_SIZE, total)}</span>
                &nbsp;of&nbsp;
                <span className="font-mono-data text-[hsl(var(--foreground))]">{total}</span>
              </p>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={safePage === 1}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium bg-[hsl(var(--muted))] hover:bg-[hsl(var(--muted))]/80 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  data-testid="changelog-prev-btn"
                >
                  <ChevronLeft className="w-3.5 h-3.5" /> Prev
                </button>
                <span className="px-3 py-1.5 text-xs font-mono-data text-[hsl(var(--muted-foreground))]">
                  Page <span className="text-[hsl(var(--foreground))] font-bold">{safePage}</span> / {totalPages}
                </span>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={safePage === totalPages}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium bg-[hsl(var(--muted))] hover:bg-[hsl(var(--muted))]/80 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  data-testid="changelog-next-btn"
                >
                  Next <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
